import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Sparkles, Play } from 'lucide-react';
import IWantThisModal from './IWantThisModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * VideoCaseStudyModal — fullscreen overlay with video on the left and
 * ideation / goal / approach panel on the right. Video only loads when open.
 *
 * Mobile correctness notes
 * ------------------------
 * • Rendered into a Portal so it's a top-level sibling of <body> — frees us
 *   from any parent's stacking-context / transform that would otherwise
 *   misanchor a fixed element.
 * • The overlay is the scroll container (overflow-y:auto). On every fresh
 *   open we force its scrollTop to 0 in a layout effect so the user never
 *   lands halfway down a tall modal.
 * • Body scroll is locked with a CSS class only (no body.position:fixed
 *   hack) — that hack caused iOS Safari to mis-scale the layout viewport,
 *   forcing the user to pinch-out to see the modal.
 */
const VideoCaseStudyModal = ({ example, onClose }) => {
  const [iwt, setIwt] = useState(false);
  // Don't load the iframe until the user actually taps play. Prevents the
  // ugly YouTube "Video unavailable" screen from flashing the moment the
  // modal opens for any private/deleted/region-locked videos, and also keeps
  // the page snappy by skipping a full third-party iframe boot on mount.
  const [playing, setPlaying] = useState(false);
  const overlayRef = useRef(null);

  // Reset state + scroll BEFORE the browser paints the new modal. Using
  // useLayoutEffect ensures the user never sees a 1-frame flash of a
  // mid-scrolled overlay.
  useLayoutEffect(() => {
    if (!example) return;
    setPlaying(false);
    if (overlayRef.current) {
      overlayRef.current.scrollTop = 0;
    }
    // Belt-and-suspenders: also scroll the window itself to the top of the
    // modal area on the next frame, in case any browser added smooth scroll
    // restoration behaviour.
    requestAnimationFrame(() => {
      if (overlayRef.current) overlayRef.current.scrollTop = 0;
    });
  }, [example?.id]);

  useEffect(() => {
    if (!example) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    // Simple body-overflow lock. This is the iOS-safe way to stop background
    // scroll without breaking the layout viewport — anything more aggressive
    // (position:fixed on body) causes Safari to render at the wrong scale.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, example]);

  if (!example) return null;

  // Normalize YouTube/Vimeo into embed urls; leave anything else as-is.
  const embedUrl = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
        return `https://www.youtube-nocookie.com/embed/${u.searchParams.get('v')}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
      }
      if (u.hostname.includes('youtu.be')) {
        return `https://www.youtube-nocookie.com/embed${u.pathname}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
      }
      if (u.hostname.includes('vimeo.com')) {
        // Preserve unlisted-video hash — Vimeo refuses to play private
        // videos without it (`vimeo.com/{id}/{hash}` → `?h={hash}`).
        const parts = u.pathname.split('/').filter(Boolean);
        const id = parts.find((p) => /^\d+$/.test(p));
        const priv = parts[parts.indexOf(id) + 1];
        const base = `https://player.vimeo.com/video/${id}`;
        return priv
          ? `${base}?h=${priv}&autoplay=1`
          : `${base}?autoplay=1`;
      }
      return url + (url.includes('?') ? '&' : '?') + 'autoplay=1';
    } catch {
      return url;
    }
  };

  const isDirectVideo = example.video_url && (
    /\.(mp4|webm|mov|m4v)(\?|$)/i.test(example.video_url) ||
    example.video_url.startsWith('/api/files/')
  );
  const directSrc = isDirectVideo ? resolveUrl(example.video_url) : null;
  const posterSrc = example.poster ? resolveUrl(example.poster) : null;
  // Short Form content (TikTok, Reels, vertical YouTube Shorts) is 9:16.
  // Everything else (sit-downs, brand films, docs, podcasts) is 16:9.
  const isShortForm = (example.category || '').toLowerCase().includes('short');
  const aspectClass = isShortForm ? 'aspect-[9/16] max-h-[70vh]' : 'aspect-video';

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm overflow-y-auto overscroll-contain"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      {/* items-start on mobile so the modal anchors to TOP — user sees the
          title + video immediately instead of having to scroll past empty
          centered whitespace. On desktop it stays centered. */}
      <div className="min-h-full flex items-start sm:items-center justify-center p-2 sm:p-4 lg:p-8">
        <div
          className="relative w-full max-w-7xl bg-[#0a0a0a] rounded-2xl overflow-hidden ring-1 ring-white/10 my-1 sm:my-4"
          onClick={(e) => e.stopPropagation()}
          data-testid="video-modal"
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white border border-white/20 text-white hover:text-black flex items-center justify-center transition"
            aria-label="Close"
            data-testid="video-modal-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* VIDEO — full width on mobile (no forced min-height), 3/5 on desktop */}
            <div className="lg:col-span-3 bg-black flex items-center justify-center lg:min-h-[85vh]">
              <div className="w-full flex items-center justify-center p-0 sm:p-3 lg:p-4">
                {example.video_url ? (
                  isDirectVideo ? (
                    <video
                      src={directSrc}
                      controls autoPlay playsInline
                      poster={posterSrc}
                      className={`w-full ${isShortForm ? 'max-h-[70vh]' : 'max-h-[60vh] lg:max-h-[80vh]'} sm:rounded-lg`}
                    />
                  ) : playing ? (
                    <div className={`w-full ${aspectClass} overflow-hidden sm:rounded-lg`}>
                      <iframe
                        src={embedUrl(example.video_url)}
                        title={example.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  ) : (
                    // Poster-first card. One tap → loads the iframe + autoplays.
                    // If the underlying YouTube/Vimeo URL is dead, the user
                    // still sees the poster + headline cleanly first.
                    <button
                      type="button"
                      onClick={() => setPlaying(true)}
                      className={`group relative w-full ${aspectClass} overflow-hidden sm:rounded-lg bg-gradient-to-br from-gray-900 to-black ring-1 ring-white/10 hover:ring-yellow-300/60 transition`}
                      data-testid="video-modal-play-btn"
                      aria-label={`Play ${example.title}`}
                    >
                      {posterSrc && (
                        <img
                          src={posterSrc}
                          alt={example.title}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-yellow-300/95 text-black shadow-2xl group-hover:scale-110 transition-transform">
                          <Play className="w-7 h-7 fill-black ml-1" />
                        </span>
                      </div>
                      <span className="absolute bottom-3 left-3 right-3 text-left text-white font-italic text-xs tracking-wider uppercase opacity-80">
                        Tap to play
                      </span>
                    </button>
                  )
                ) : (
                  <div className="text-center text-gray-500 py-16">
                    <p className="font-body text-sm">No video URL set</p>
                    <p className="text-xs mt-2">Add one in the FLYT admin → Examples</p>
                  </div>
                )}
              </div>
            </div>

            {/* PANEL — 2/5 right (full width on mobile, stacks below video) */}
            <div className="lg:col-span-2 p-5 sm:p-7 lg:p-10 text-white flex flex-col gap-5 lg:gap-6 lg:overflow-y-auto lg:max-h-[85vh]">
              {example.client_name && (
                <p className="font-italic text-yellow-300 text-xs tracking-widest uppercase">
                  FLYT Studios · {example.client_name}
                </p>
              )}
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase leading-[0.95]" data-testid="modal-title">
                {example.title}
              </h2>

              {example.ideation && (
                <div>
                  <p className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2">The Ideation</p>
                  <p className="font-body text-sm lg:text-base text-gray-200 leading-relaxed">{example.ideation}</p>
                </div>
              )}

              {example.goal && (
                <div>
                  <p className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2">The Goal</p>
                  <p className="font-body text-sm lg:text-base text-gray-200 leading-relaxed">{example.goal}</p>
                </div>
              )}

              {example.approach && (
                <div>
                  <p className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2">The Approach</p>
                  <p className="font-body text-sm lg:text-base text-gray-200 leading-relaxed">{example.approach}</p>
                </div>
              )}

              {(example.stats || example.credits) && (
                <div className="border-t border-white/10 pt-5 space-y-3">
                  {example.stats && (
                    <div>
                      <p className="font-display text-[10px] uppercase tracking-widest text-gray-500 mb-1">Results</p>
                      <p className="font-body text-sm text-gray-300">{example.stats}</p>
                    </div>
                  )}
                  {example.credits && (
                    <div>
                      <p className="font-display text-[10px] uppercase tracking-widest text-gray-500 mb-1">Credits</p>
                      <p className="font-body text-sm text-gray-300">{example.credits}</p>
                    </div>
                  )}
                </div>
              )}

              {example.video_url && !isDirectVideo && (
                <a
                  href={example.video_url}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-yellow-300 hover:text-white transition pt-4"
                >
                  Open on original platform <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}

              {/* I WANT THIS — primary conversion CTA */}
              <button
                type="button"
                onClick={() => setIwt(true)}
                className="mt-auto w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-yellow-300 hover:bg-yellow-200 text-black font-bold transition group"
                data-testid="i-want-this-btn"
              >
                <Sparkles className="w-4 h-4" />
                I want this — get a quoted deck
              </button>
            </div>
          </div>
        </div>
      </div>

      <IWantThisModal example={iwt ? example : null} onClose={() => setIwt(false)} />
    </div>,
    document.body
  );
};

export default VideoCaseStudyModal;
